import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getRenderedScale } from '../../utils';

const minScale = 0.8;
const maxScale = 3;

const clamp = (val, min, max) => Math.max(min, Math.min(val, max));

function ImagePanAndZoom(props) {
  const {
    className,
    style,
    children,
    onClick,
    alt = 'image',
    src,
    enable = true,
  } = props;
  const parentRef = useRef(null);
  // parent div size info:
  const [parentSize, setParentSize] = useState({ width: 0, height: 0 });
  // previous touch event info:
  const [prevTouch, setPrevTouch] = useState(null);
  // Current image scale and offset:
  const [scale, setScale] = useState(0.9);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  // For mouse-based dragging:
  const isMouseDown = useRef(false);
  const prevMousePos = useRef(null);
  // Image size
  // const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  useEffect(
    () => {
      if (parentRef.current) {
      // Layout px, like the translate offsets, so clamping holds under the shell zoom
        setParentSize({
          width: parentRef.current.offsetWidth,
          height: parentRef.current.offsetHeight,
        });
      }
    },
    [style],
  );

  const clampTranslation = useCallback(
    () => {
    // If parent width or height is 0, we can’t do the math, so just skip
      if (!parentSize.width || !parentSize.height) return;

      // The image is effectively the same size as the parent at scale=1,
      // so scaledWidth = parentWidth * scale, likewise for height.
      const scaledWidth = parentSize.width * scale;
      const scaledHeight = parentSize.height * scale;

      // We want to keep the image fully in view.
      // If the image is larger than the parent, we allow some panning,
      // but not so much that the image goes fully outside.
      // If the scaled image is smaller or equal to the parent, we’ll just center it (translate=0).

      let maxOffsetX = 0;
      let maxOffsetY = 0;

      if (scaledWidth > parentSize.width) {
        maxOffsetX = (scaledWidth - parentSize.width) / 2;
      }
      if (scaledHeight > parentSize.height) {
        maxOffsetY = (scaledHeight - parentSize.height) / 2;
      }

      const clampedX = clamp(translate.x, -maxOffsetX, maxOffsetX);
      const clampedY = clamp(translate.y, -maxOffsetY, maxOffsetY);

      // Only update state if values actually changed
      if (clampedX !== translate.x || clampedY !== translate.y) {
        setTranslate({ x: clampedX, y: clampedY });
      }
    },
    [parentSize.height, parentSize.width, scale, translate.x, translate.y],
  );

  const handleTouchStart = (event) => {
    setPrevTouch(event?.touches);
  };

  const handleTouchMove = (event) => {
    // event.preventDefault();

    if ((event.touches.length === 2) && (prevTouch?.length === 2)) {
      const prevDistance = Math.sqrt(
        (prevTouch[0].clientX - prevTouch[1].clientX) ** 2
        + (prevTouch[0].clientY - prevTouch[1].clientY) ** 2,
      );

      const newDistance = Math.sqrt(
        (event.touches[0].clientX - event.touches[1].clientX) ** 2
        + (event.touches[0].clientY - event.touches[1].clientY) ** 2,
      );

      const scaleDelta = newDistance / prevDistance;

      const newScale = Math.max(
        minScale,
        Math.min(scale * scaleDelta, maxScale),
      ); // Limit zoom between 1x and 3x

      setScale(newScale);
    } else if ((event.touches.length === 1) && (prevTouch?.length === 1)) {
      // Pointer deltas are visual px, translate is layout px: divide out the shell zoom
      const renderedScale = getRenderedScale(parentRef.current);
      const deltaX = (event.touches[0].clientX - prevTouch[0].clientX) / renderedScale;
      const deltaY = (event.touches[0].clientY - prevTouch[0].clientY) / renderedScale;

      setTranslate({ x: translate.x + deltaX, y: translate.y + deltaY });
    }

    setPrevTouch(event.touches);
  };

  const handleTouchEnd = () => {
    // Make sure we'll recapture the start location if touch event resumes
    setPrevTouch(null);
    // Once the user lifts a finger, clamp the final position
    clampTranslation();
  };

  /**
   * MOUSE HANDLERS
   */
  const handleMouseDown = (event) => {
    if (enable) {
      isMouseDown.current = true;
      prevMousePos.current = { x: event.clientX, y: event.clientY };
    }
  };

  const handleMouseMove = (event) => {
    if (!enable) return;
    if (!isMouseDown.current) return;

    // Pointer deltas are visual px, translate is layout px: divide out the shell zoom
    const renderedScale = getRenderedScale(parentRef.current);
    const deltaX = (event.clientX - prevMousePos.current.x) / renderedScale;
    const deltaY = (event.clientY - prevMousePos.current.y) / renderedScale;

    setTranslate((prevTranslate) => ({
      x: prevTranslate.x + deltaX,
      y: prevTranslate.y + deltaY,
    }));

    prevMousePos.current = { x: event.clientX, y: event.clientY };
  };

  const handleMouseUp = () => {
    if (enable) {
      isMouseDown.current = false;
      prevMousePos.current = null;
      clampTranslation();
    }
  };

  /**
   * ZOOM VIA WHEEL
   */
  const handleWheel = (event) => {
    // console.log(`Zoom ${event.deltaY}`);
    // event.preventDefault();
    const newScale = Math.max(
      minScale,
      Math.min(scale - (event.deltaY / 600), maxScale),
    );

    setScale(newScale);
  };

  useEffect(
    () => { clampTranslation(); },
    [scale, parentSize, clampTranslation],
  );

  const handleKeyDown = () => null;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={parentRef}
      className={classNames(
        'flex items-center justify-center overflow-hidden',
        className,
      )}
      style={style}
      onClick={onClick}
      id="imageBackground"
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        // className="h-full w-full border border-pxUiAlert flex items-center justify-center"
        className="h-full w-full flex items-center justify-center"
        style={{
          transform: `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`,
          transformOrigin: 'center center',
          touchAction: 'none',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <img
          className="object-contain m-auto max-w-full max-h-full"
          alt={alt}
          src={src}
        />
      </div>

      {/* Optional children: */}
      {children}
    </div>
  );
}

ImagePanAndZoom.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  onClick: PropTypes.func,
  children: PropTypes.node,
  alt: PropTypes.string,
  src: PropTypes.string.isRequired,
  enable: PropTypes.bool,
};

export default ImagePanAndZoom;
